import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from './Navbar';
import Footer from './Footer';
import AIBadge from './AIBadge';
import HowItWorks from './pages/HowItWorks';
import ProductShowcaseVideo from './ProductShowcaseVideo';
import GlowCard from './GlowCard';
import ProductCarouselSection from './ProductCarouselSection';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  ZoomIn, 
  Printer, 
  BookOpen, 
  Package, 
  Truck, 
  ChevronDown,
  Combine,
  Scissors,
  Minimize2,
  LayoutGrid,
  FileImage,
  Images,
  Hash,
  Moon,
  Crop,
  Timer,
  ArrowUpRight
} from 'lucide-react';
import { trackEvent } from '../utils/analytics';
import { useAuth } from '../contexts/AuthContext';
import { apiGet } from '../services/api';
import WeekendSaleBanner from './WeekendSaleBanner';

interface ModernLandingPageProps {
  isStandalone?: boolean;
  onNavigate?: (page: string) => void;
}

const PRINTS_SLIDES = [
  { id: 'photo2', label: 'Crystal Clear Document Printing', duration: 3000, src: '/ncp-gallery/2.webp' },
  { id: 'photo3', label: 'Strong Golden Metal Wiro Binding', duration: 3000, src: '/ncp-gallery/3.webp' },
  { id: 'photo4', label: 'Rich Ink Quality & Contrast', duration: 3000, src: '/ncp-gallery/4.webp' },
  { id: 'photo5', label: 'Anti-bleed 80 GSM High-Brightness Sheets', duration: 3000, src: '/ncp-gallery/5.webp' },
  { id: 'photo6', label: 'Perfect Spine & Binding Alignment', duration: 3000, src: '/ncp-gallery/6.webp' },
  { id: 'photo7', label: 'High Brightness Premium Paper', duration: 3000, src: '/ncp-gallery/7.webp' },
  { id: 'photo8', label: 'Smooth Turn Wire-O Binding', duration: 3000, src: '/ncp-gallery/8.webp' },
  { id: 'photo9', label: 'Professional Quality Report Prints', duration: 3000, src: '/ncp-gallery/9.webp' },
  { id: 'photo10', label: 'Perfect Layout & Border Alignment', duration: 3000, src: '/ncp-gallery/10.webp' },
  { id: 'photo11', label: 'Durable Protective Plastic Cover', duration: 3000, src: '/ncp-gallery/11.webp' },
  { id: 'photo12', label: 'Crisp Text & Fine Detail Clarity', duration: 3000, src: '/ncp-gallery/12.webp' },
  { id: 'photo13', label: 'Perfect Corner Cuts & Finishing', duration: 3000, src: '/ncp-gallery/13.webp' },
  { id: 'photo14', label: 'Heavy Duty Binding for Daily Use', duration: 3000, src: '/ncp-gallery/14.webp' },
  { id: 'photo15', label: 'Vibrant Color & Grayscale Contrast', duration: 3000, src: '/ncp-gallery/15.webp' },
  { id: 'photo16', label: 'Elegant Design & Aesthetic Appeal', duration: 3000, src: '/ncp-gallery/16.webp' },
  { id: 'photo17', label: 'Perfect Bound Lecture Note Booklets', duration: 3000, src: '/ncp-gallery/17.webp' },
  { id: 'photo18', label: 'Thick Premium Texture Feel Sheets', duration: 3000, src: '/ncp-gallery/18.webp' },
  { id: 'photo19', label: 'Safe Transit Bubble Wrap Packaging', duration: 3000, src: '/ncp-gallery/19.webp' },
  { id: 'photo20', label: 'Delivered Crisp & Fresh to Doorstep', duration: 3000, src: '/ncp-gallery/20.webp' }
];

type CtaTab = 'prints' | 'flow' | 'tools';



const CTA_TABS: { id: CtaTab; label: string }[] = [
  { id: 'prints', label: 'PRINTS' },
  { id: 'flow', label: 'FLOW' },
  { id: 'tools', label: 'TOOLS' },
];

const PDF_TOOLS_LIST = [
  {
    id: 'merge-pdf',
    title: 'Merge PDF',
    desc: 'Combine lecture files into one',
    icon: Combine,
    url: 'https://tools.notescrafter.com/tool/merge-pdf',
    badge: 'Popular',
    color: 'from-purple-500/20 to-indigo-500/20 text-purple-400 group-hover:text-purple-300'
  },
  {
    id: 'split-pdf',
    title: 'Split PDF',
    desc: 'Extract pages and chapters',
    icon: Scissors,
    url: 'https://tools.notescrafter.com/tool/split-pdf',
    color: 'from-blue-500/20 to-indigo-500/20 text-blue-400 group-hover:text-blue-300'
  },
  {
    id: 'compress-pdf',
    title: 'Compress PDF',
    desc: 'Shrink file size for fast upload',
    icon: Minimize2,
    url: 'https://tools.notescrafter.com/tool/compress-pdf',
    badge: 'Fast',
    color: 'from-emerald-500/20 to-teal-500/20 text-emerald-400 group-hover:text-emerald-300'
  },
  {
    id: 'organize-pdf',
    title: 'Organize PDF',
    desc: 'Reorder and restructure pages',
    icon: LayoutGrid,
    url: 'https://tools.notescrafter.com/tool/organize-pdf',
    color: 'from-violet-500/20 to-purple-500/20 text-violet-400 group-hover:text-violet-300'
  },
  {
    id: 'pdf-to-jpg',
    title: 'PDF to Images',
    desc: 'Convert sheets to crisp JPGs',
    icon: FileImage,
    url: 'https://tools.notescrafter.com/tool/pdf-to-jpg',
    color: 'from-amber-500/20 to-orange-500/20 text-amber-400 group-hover:text-amber-300'
  },
  {
    id: 'jpg-to-pdf',
    title: 'Images to PDF',
    desc: 'Turn photos into clean PDF',
    icon: Images,
    url: 'https://tools.notescrafter.com/tool/jpg-to-pdf',
    color: 'from-rose-500/20 to-pink-500/20 text-rose-400 group-hover:text-rose-300'
  },
  {
    id: 'add-page-numbers',
    title: 'Page Numbers',
    desc: 'Stamp counters on study sheets',
    icon: Hash,
    url: 'https://tools.notescrafter.com/tool/add-page-numbers',
    color: 'from-cyan-500/20 to-sky-500/20 text-cyan-400 group-hover:text-cyan-300'
  },
  {
    id: 'invert-pdf',
    title: 'Invert PDF',
    desc: 'Convert sheets to dark mode',
    icon: Moon,
    url: 'https://tools.notescrafter.com/tool/invert-pdf',
    color: 'from-indigo-500/20 to-purple-500/20 text-indigo-400 group-hover:text-indigo-300'
  },
  {
    id: 'crop-pdf',
    title: 'Crop PDF',
    desc: 'Trim margins and crop sheets',
    icon: Crop,
    url: 'https://tools.notescrafter.com/tool/crop-pdf',
    color: 'from-teal-500/20 to-emerald-500/20 text-teal-400 group-hover:text-teal-300'
  },
  {
    id: 'pomodoro',
    title: 'Focus Timer',
    desc: 'Jee & Neet study clock session',
    icon: Timer,
    url: 'https://tools.notescrafter.com/tool/pomodoro',
    badge: 'Study',
    color: 'from-red-500/20 to-amber-500/20 text-red-400 group-hover:text-red-300'
  }
];

const TAB_DURATIONS: Record<CtaTab, number> = {
  prints: 20000, // 20 seconds for PRINTS
  flow: 15000,   // 15 seconds for FLOW
  tools: 10000,  // 10 seconds for TOOLS
};

const PRINT_PROCESS_STEPS = [
  {
    id: 1,
    title: "1. Printing",
    icon: Printer,
    points: [
      "Inkjet printing with production-grade commercial printers",
      "75 GSM premium high-brightness paper for sharp, crisp output",
      "Duplex (double-sided) printing on every page",
    ]
  },
  {
    id: 2,
    title: "2. Binding",
    icon: BookOpen,
    points: [
      "Golden premium double-loop wire-o binding with square holes",
      "Front cover: Semi-transparent frosted PVC — 400 micron thickness",
      "Back cover: Black frosted premium PVC — 400 micron thickness",
    ]
  },
  {
    id: 3,
    title: "3. Packing",
    icon: Package,
    points: [
      "Single notebooks sealed in 75 micron premium courier bags",
      "Each notebook protected with honeycomb wrap cushioning",
      "Bulk orders curated inside sturdy boxes with honeycomb padding",
    ]
  },
  {
    id: 4,
    title: "4. Courier",
    icon: Truck,
    points: [
      "Powered by Shiprocket — India's leading courier aggregator",
      "Delivered via Amazon Shipping, Blue Dart & Delhivery",
      "Real-time shipment tracking updates on every order",
    ]
  }
];

const ModernLandingPage: React.FC<ModernLandingPageProps> = ({ isStandalone = true, onNavigate }) => {
  const { signIn, isAuthenticated, isLoading } = useAuth();
  const [printsSlideIndex, setPrintsSlideIndex] = useState(0);
  const [printsInteracted, setPrintsInteracted] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState<number | null>(null);

  // 3 CTA slideshow state
  const [activeCtaTab, setActiveCtaTab] = useState<CtaTab>('prints');
  const [ctaPaused, setCtaPaused] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);

  const [isZoomed, setIsZoomed] = useState(false);

  // DB dynamic rates state (seeded with exact DB default rates)
  const [dbRates, setDbRates] = useState<Record<string, { bw: number; color: number }>>({
    guest: { bw: 1.25, color: 3.35 },
    monthly: { bw: 1.15, color: 3.00 },
    half_yearly: { bw: 1.05, color: 2.75 },
    annual: { bw: 0.99, color: 2.35 },
    lifetime: { bw: 0.89, color: 1.99 }
  });

  // Fetch dynamic print rates from DB (with fallback to live production endpoint)
  useEffect(() => {
    let mounted = true;
    const fetchRates = async () => {
      try {
        const res = await apiGet<{ success: boolean; data: Record<string, { bw: number; color: number }> }>('/api/print/rates');
        if (mounted && res?.success && res?.data) {
          setDbRates(res.data);
          return;
        }
      } catch {
        // In local development or if proxy is offline, fetch directly from live production API
        try {
          const fallbackRes = await fetch('https://notescrafter.com/api/print/rates').then(r => r.json());
          if (mounted && fallbackRes?.success && fallbackRes?.data) {
            setDbRates(fallbackRes.data);
            return;
          }
        } catch {
          // Keep default database rates
        }
      }
    };
    fetchRates();
    return () => {
      mounted = false;
    };
  }, []);

  const getBwRate = (key: string | null) => {
    const plan = key === null ? 'guest' : key;
    if (dbRates && dbRates[plan]?.bw !== undefined) {
      return `₹${dbRates[plan].bw.toFixed(2)}`;
    }
    return key === null ? '₹1.25' :
      key === 'monthly' ? '₹1.15' :
      key === 'half_yearly' ? '₹1.05' :
      key === 'annual' ? '₹0.99' : '₹0.89';
  };

  const getColorRate = (key: string | null) => {
    const plan = key === null ? 'guest' : key;
    if (dbRates && dbRates[plan]?.color !== undefined) {
      return `₹${dbRates[plan].color.toFixed(2)}`;
    }
    return key === null ? '₹3.35' :
      key === 'monthly' ? '₹3.00' :
      key === 'half_yearly' ? '₹2.75' :
      key === 'annual' ? '₹2.35' : '₹1.99';
  };

  // Smart Autoplay Slide Cycle for NotesCrafter Prints gallery (3s duration for all slides)
  useEffect(() => {
    if (printsInteracted) return;

    const timer = setTimeout(() => {
      setPrintsSlideIndex((prev) => (prev + 1) % PRINTS_SLIDES.length);
    }, 3000);

    return () => clearTimeout(timer);
  }, [printsSlideIndex, printsInteracted]);

  // Auto-resume autoplay after 10 seconds of no manual interaction
  useEffect(() => {
    if (!printsInteracted) return;

    const resumeTimer = setTimeout(() => {
      setPrintsInteracted(false);
    }, 10000);

    return () => clearTimeout(resumeTimer);
  }, [printsInteracted]);

  // Handle keyboard navigation in Zoom Mode (Escape to close, Left/Right arrows to navigate)
  useEffect(() => {
    if (!isZoomed) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsZoomed(false);
      } else if (e.key === 'ArrowLeft') {
        setPrintsInteracted(true);
        setPrintsSlideIndex((prev) => (prev - 1 + PRINTS_SLIDES.length) % PRINTS_SLIDES.length);
      } else if (e.key === 'ArrowRight') {
        setPrintsInteracted(true);
        setPrintsSlideIndex((prev) => (prev + 1) % PRINTS_SLIDES.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isZoomed]);

  // Auto-play slideshow with custom per-tab durations (20s PRINTS, 15s FLOW, 10s TOOLS)
  useEffect(() => {
    if (ctaPaused || userInteracted || printsInteracted) return;

    const duration = TAB_DURATIONS[activeCtaTab];
    const timer = setTimeout(() => {
      setActiveCtaTab((prev) => {
        if (prev === 'prints') return 'flow';
        if (prev === 'flow') return 'tools';
        return 'prints';
      });
    }, duration);

    return () => clearTimeout(timer);
  }, [activeCtaTab, ctaPaused, userInteracted, printsInteracted]);

  return (
    <div className="min-h-screen flex flex-col text-text-main overflow-x-hidden w-full max-w-full flex-nowrap">
      {/* Render Navbar only when used as standalone page (used outside App shell) */}
      {isStandalone && <Navbar />}

      {/* Weekend Flash Sale Banner */}
      <div className="w-full pt-2 md:pt-4">
        <WeekendSaleBanner />
      </div>

      {/* Main content: hero is shown whether embedded or standalone; Navbar/Footer remain conditional */}
      <main className="flex-1 overflow-x-hidden max-w-full">
        <div className={`w-full px-0 ${isStandalone ? 'pt-20 md:pt-28 pb-16' : 'pt-2 pb-4'}`}>
          {/* Hero Heading Section */}
          <div className="relative mb-8 text-center px-3 sm:px-6">
            <h3 className="hero-heading tracking-tight relative flex flex-col items-center animate-slideUp text-center select-none">
              {/* Larger, elegant uppercase sub-tagline */}
              <span className="block text-text-main font-bold text-sm sm:text-base md:text-lg lg:text-xl mb-4 tracking-[0.3em] uppercase opacity-95">
                REIMAGINING THE
              </span>

              {/* Tagline using brand gradient pan animation */}
              <span className="relative block text-transparent bg-clip-text bg-[linear-gradient(135deg,#A855F7_0%,#6366F1_50%,#A855F7_100%)] bg-[length:200%_auto] animate-gradient-pan font-black pb-2 text-3xl sm:text-4xl md:text-6xl lg:text-7xl leading-[1.05] drop-shadow-[0_2px_30px_rgba(168,85,247,0.2)]">
                Student Learning Experience
              </span>
            </h3>

            {/* Product Link Cards — Premium Carousel Section */}
            <ProductCarouselSection />

            {/* Call to Action Buttons - Same line */}
            {!isAuthenticated && (
              <div className="flex flex-col sm:flex-row justify-center items-center gap-3 mt-6 mb-2">
                {/* Dynamic Sign in with Google Button (Left) */}
                <button
                  onClick={() => {
                    trackEvent('sign_in_clicked', {
                      source: 'hero_above_stats',
                      device_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
                    });
                    signIn();
                  }}
                  className="group relative w-full sm:w-auto overflow-hidden bg-gradient-to-r from-primary to-secondary hover:brightness-110 text-white font-bold h-[54px] px-8 rounded-xl shadow-lg shadow-primary/20 hover:shadow-xl transition-all duration-300 transform hover:scale-[1.01] flex items-center justify-center gap-3"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  <div className="relative flex items-center justify-center gap-3">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    <span>Sign in with Google</span>
                  </div>
                </button>

                {/* How it Works Button (Right) -> Youtube channel @notescrafterapp */}
                <a
                  href="https://www.youtube.com/@notescrafterapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackEvent('how_it_works_clicked', {
                      source: 'hero_section_youtube',
                      device_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
                    });
                  }}
                  className="group px-7 py-3.5 bg-black/5 dark:bg-white/5 backdrop-blur-md border border-black/10 dark:border-white/10 hover:border-red-500/50 text-text-main font-semibold text-sm sm:text-base rounded-xl transition-all duration-300 w-full sm:w-auto shadow-md hover:shadow-red-500/10 flex items-center justify-center gap-2.5 h-[54px]"
                >
                  {/* YouTube Red Icon */}
                  <svg className="w-5 h-5 text-red-500 fill-current" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  <span>How it Works</span>
                  <ArrowRightIcon className="w-4 h-4 text-text-muted group-hover:text-text-main group-hover:translate-x-1 transition-all duration-300" />
                </a>
              </div>
            )}
          </div>

          {/* Stats — compact single row */}
          <div className="mt-6 mb-2 w-full px-3 sm:px-6 lg:px-12">
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm sm:text-base font-medium">
              <span className="flex items-center gap-1.5 text-text-muted whitespace-nowrap">
                <svg className="w-3.5 h-3.5 text-primary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                <span className="text-text-main font-semibold">1M+</span>
                <span className="text-text-muted">files processed</span>
              </span>
              <span className="text-primary/40 select-none hidden sm:inline">·</span>
              <span className="flex items-center gap-1.5 text-text-muted whitespace-nowrap">
                <svg className="w-3.5 h-3.5 text-primary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0112 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" /></svg>
                <span className="text-text-main font-semibold">2L+</span>
                <span className="text-text-muted">students helped</span>
              </span>
              <span className="text-primary/40 select-none hidden sm:inline">·</span>
              <span className="flex items-center gap-1.5 text-text-muted whitespace-nowrap">
                <svg className="w-3.5 h-3.5 text-primary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 6h11v10H3zM14 9h4l3 3v4h-7z" /><circle cx="6" cy="18" r="2" stroke="currentColor" strokeWidth={2} fill="none" /><circle cx="17" cy="18" r="2" stroke="currentColor" strokeWidth={2} fill="none" /></svg>
                <span className="text-text-main font-semibold">19K+</span>
                <span className="text-text-muted">pincodes</span>
              </span>
              <span className="text-primary/40 select-none hidden sm:inline">·</span>
              <span className="flex items-center gap-1.5 text-text-muted whitespace-nowrap">
                <svg className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500 shrink-0" viewBox="0 0 24 24"><path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.907c.961 0 1.36 1.242.588 1.81l-3.97 2.883a1 1 0 00-.364 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.971-2.883a1 1 0 00-1.18 0l-3.97 2.883c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.364-1.118L2.98 10.1c-.77-.568-.372-1.81.588-1.81h4.907a1 1 0 00.95-.69l1.519-4.674z" /></svg>
                <span className="text-text-main font-semibold">4.8</span>
                <span className="text-text-muted">app rating</span>
              </span>
            </div>
          </div>


          {/* Unified L-Shape / Stepped Container System */}
          <div 
            className="w-full px-3 sm:px-6 lg:px-10 my-6"
            onMouseEnter={() => setCtaPaused(true)}
            onMouseLeave={() => {
              if (!userInteracted) setCtaPaused(false);
            }}
          >
            {/* 3-Tab Header Row - Seamlessly Integrated with L-Shape Container */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4 w-full items-end">
              {CTA_TABS.map((tab) => {
                const isActive = activeCtaTab === tab.id;
                if (isActive) {
                  return (
                    <div
                      key={tab.id}
                      className="relative z-20 py-3 sm:py-3.5 px-3 sm:px-6 rounded-t-2xl font-bold bg-white dark:bg-[#0e0d15] text-text-main dark:text-white border-t border-x border-black/5 dark:border-transparent flex items-center justify-center gap-2 sm:gap-3 select-none w-full translate-y-[2px] shadow-sm dark:shadow-none"
                    >
                      {/* Top Base Border Track with Rounded Edges */}
                      <div className="absolute top-0 inset-x-0 h-[2px] bg-purple-500/20 rounded-t-2xl pointer-events-none" />

                      {/* Active Animated Timer Thin Line (20s for PRINTS, 15s for FLOW, 10s for TOOLS) */}
                      {!ctaPaused && !userInteracted && (
                        <motion.div
                          key={activeCtaTab}
                          initial={{ width: '0%' }}
                          animate={{ width: '100%' }}
                          transition={{ duration: TAB_DURATIONS[activeCtaTab] / 1000, ease: 'linear' }}
                          className="absolute top-0 left-0 h-[2.5px] bg-gradient-to-r from-purple-400 via-indigo-400 to-purple-300 rounded-t-2xl shadow-[0_0_20px_rgba(168,85,247,0.95)] z-30 pointer-events-none"
                        />
                      )}
                      
                      <img 
                        src="/logo.v2.png" 
                        alt="" 
                        className="h-6 sm:h-7 md:h-8 w-auto shrink-0" 
                        style={{ filter: 'drop-shadow(0 0 6px rgba(192, 132, 252, 0.5))' }} 
                      />
                      <span className="text-sm sm:text-base md:text-lg font-black tracking-widest text-text-main dark:text-white uppercase drop-shadow-[0_2px_10px_rgba(168,85,247,0.3)]">
                        {tab.label}
                      </span>
                      
                      {/* Tab bridge: seamless bottom connection into main container */}
                      <div className="absolute -bottom-[3px] inset-x-0 h-[5px] bg-white dark:bg-[#0e0d15] z-30 pointer-events-none" />
                    </div>
                  );
                }

                // Inactive tabs: completely containerless (no background box or border)
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveCtaTab(tab.id);
                      setUserInteracted(true);
                      setCtaPaused(true);
                    }}
                    onMouseEnter={() => setCtaPaused(true)}
                    onMouseLeave={() => {
                      if (!userInteracted) setCtaPaused(false);
                    }}
                    className="relative z-10 py-3 sm:py-3.5 px-3 sm:px-6 font-bold text-text-muted hover:text-text-main dark:hover:text-white bg-transparent border-none transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 select-none w-full group cursor-pointer"
                  >
                    <img 
                      src="/logo.v2.png" 
                      alt="" 
                      className="h-5 sm:h-6 md:h-7 w-auto shrink-0 opacity-40 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300" 
                    />
                    <span className="text-sm sm:text-base md:text-lg font-extrabold tracking-widest text-text-muted/70 group-hover:text-text-main dark:group-hover:text-white uppercase transition-colors duration-300">
                      {tab.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Main Container Body (Seamless L-Shape / Stepped Integration) */}
            <div className="relative z-10 w-full">
              {/* Single Glowing Line across Top Border of Main Container with Rounded Edges */}
              <div className="absolute -top-[1px] inset-x-0 h-[2px] bg-gradient-to-r from-purple-600/40 via-indigo-500/80 to-purple-600/40 rounded-t-2xl shadow-[0_0_20px_rgba(168,85,247,0.6)] pointer-events-none z-10" />

              <AnimatePresence mode="wait">
                {/* 1. PRINTS CTA Container (L-shape merged on Left) */}
                {activeCtaTab === 'prints' && (
                  <motion.div
                    key="prints"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="w-full"
                  >
                    <GlowCard className="w-full rounded-tl-none rounded-tr-2xl rounded-b-2xl !overflow-visible" contentClassName="p-4 sm:p-6 lg:p-10 shadow-2xl rounded-tl-none rounded-tr-[11px] rounded-b-[11px] !overflow-visible">
                      <div className="w-full flex flex-col lg:flex-row items-start justify-between gap-6 lg:gap-12 py-1">
                        {/* LEFT COLUMN: Gallery Viewport with Photos & Thumbnails */}
                        <div className="w-full lg:w-1/2 shrink-0 flex flex-col items-center justify-start relative select-none">
                          {/* Main Viewport: Full Size 100% Width 1:1 Square Photo Carousel with Zoom In */}
                          <div 
                            onClick={() => setIsZoomed(true)}
                            className="w-full aspect-square flex items-center justify-center relative overflow-hidden rounded-2xl border border-black/10 dark:border-white/10 shadow-2xl bg-black/5 dark:bg-black/40 group/preview cursor-zoom-in"
                          >
                            <div className="w-full h-full flex items-center justify-center transition-opacity duration-300 relative">
                              <img
                                key={printsSlideIndex}
                                src={PRINTS_SLIDES[printsSlideIndex].src}
                                alt={PRINTS_SLIDES[printsSlideIndex].label}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover/preview:scale-105 animate-fadeIn"
                                fetchPriority="high"
                              />
                            </div>

                            {/* Click to Zoom Pill on Hover */}
                            <div className="absolute top-3 right-3 z-20 px-2.5 py-1 rounded-full bg-black/60 border border-white/15 text-white/90 text-[10px] font-bold tracking-wider backdrop-blur-md opacity-0 group-hover/preview:opacity-100 transition-opacity flex items-center gap-1 shadow-lg pointer-events-none">
                              <ZoomIn className="w-3.5 h-3.5 text-purple-300" />
                              <span>Click to Zoom</span>
                            </div>

                            {/* Left Navigation Arrow */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setPrintsInteracted(true);
                                setPrintsSlideIndex((prev) => (prev - 1 + PRINTS_SLIDES.length) % PRINTS_SLIDES.length);
                              }}
                              className="absolute left-3 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center w-9 h-9 rounded-full bg-black/70 hover:bg-black/90 border border-white/20 text-white opacity-80 md:opacity-0 group-hover/preview:opacity-100 transition-all duration-300 hover:scale-105 active:scale-95 shadow-lg cursor-pointer"
                              aria-label="Previous slide"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                              </svg>
                            </button>

                            {/* Right Navigation Arrow */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setPrintsInteracted(true);
                                setPrintsSlideIndex((prev) => (prev + 1) % PRINTS_SLIDES.length);
                              }}
                              className="absolute right-3 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center w-9 h-9 rounded-full bg-black/70 hover:bg-black/90 border border-white/20 text-white opacity-80 md:opacity-0 group-hover/preview:opacity-100 transition-all duration-300 hover:scale-105 active:scale-95 shadow-lg cursor-pointer"
                              aria-label="Next slide"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </div>

                          {/* All Micro-thumbnails Display with Horizontal Scroll */}
                          <div className="w-full flex flex-nowrap overflow-x-auto gap-2 mt-4 py-2 px-1 scrollbar-thin scrollbar-thumb-purple-500/20 scrollbar-track-transparent justify-start sm:justify-center items-center">
                            {PRINTS_SLIDES.map((slide, idx) => (
                              <button
                                key={slide.id}
                                onClick={() => {
                                  setPrintsSlideIndex(idx);
                                  setPrintsInteracted(true);
                                }}
                                className={`relative w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-lg overflow-hidden border transition-all duration-300 transform hover:scale-110 focus:outline-none cursor-pointer shrink-0 ${
                                  idx === printsSlideIndex
                                    ? 'border-purple-500 ring-2 ring-purple-500/50 scale-105 shadow-md shadow-purple-500/30'
                                    : 'border-black/10 dark:border-white/10 hover:border-black/30 dark:hover:border-white/30 opacity-70 hover:opacity-100'
                                }`}
                              >
                                <img src={slide.src} alt={slide.label} className="w-full h-full object-cover" />
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* RIGHT COLUMN: Full Width Content */}
                        <div className="w-full lg:w-1/2 flex flex-col justify-start items-center lg:items-start text-center lg:text-left z-10 px-0">
                          {/* 1. Main Headline */}
                          <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 leading-tight text-text-main">
                            <span className="block mb-1 text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-text-main">We</span>
                            <span className="block text-text-main drop-shadow-sm text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl">
                              Print.Bind.Deliver
                            </span>
                          </h1>

                          {/* 2. 5-Star Social Proof Badge */}
                          <div className="flex items-center gap-3 mb-5 justify-center lg:justify-start">
                            <div className="flex items-center gap-0.5">
                              {[...Array(5)].map((_, i) => (
                                <svg key={i} className="w-4 h-4 text-amber-400 drop-shadow-[0_0_4px_rgba(251,191,36,0.6)]" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              ))}
                            </div>
                            <span className="text-xs sm:text-sm font-semibold text-text-muted">
                              <span className="text-text-main font-extrabold">50+</span> Happy Students
                            </span>
                            <span className="text-text-muted/40 text-xs">•</span>
                            <span className="text-xs text-text-muted/70 font-medium">5.0 Rating</span>
                          </div>

                          {/* 3. Subtext Description */}
                          <p className="text-sm sm:text-base md:text-lg text-text-muted mb-6 w-full font-light leading-relaxed tracking-wide">
                            Upload your PDF, choose print specs, and let us handle the rest. We create premium notebooks and deliver them to your doorstep – made for Indian students. 🇮🇳
                          </p>

                          {/* 4. Combined Free Shipping & EDD Delivery Card */}
                          <div className="w-full mb-6 rounded-2xl bg-emerald-500/10 dark:bg-[#0a1a14] border border-emerald-500/30 shadow-[0_0_20px_rgba(16,185,129,0.12)] relative overflow-hidden flex flex-row items-center text-left hover:border-emerald-500/50 transition-colors duration-300 min-h-[56px] sm:min-h-[64px]">
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(16,185,129,0.1),transparent_50%)] pointer-events-none" />
                            {/* Left Compartment (Icon) */}
                            <div className="relative z-10 w-14 self-stretch bg-gradient-to-br from-emerald-500/20 to-teal-500/20 text-emerald-600 dark:text-emerald-300 flex items-center justify-center border-r border-emerald-500/30 shrink-0">
                              <Truck className="w-5 h-5" />
                            </div>
                            {/* Right Compartment (Text) */}
                            <div className="relative z-10 flex-1 px-4 py-2 sm:py-3 flex flex-col justify-center">
                              <span className="text-sm font-extrabold text-emerald-950 dark:text-white tracking-wide uppercase flex items-center gap-1.5 leading-none">
                                Doorstep Delivery
                              </span>
                              <p className="text-xs text-emerald-900/80 dark:text-white/70 leading-relaxed font-medium mt-1">
                                Fast &amp; reliable delivery across all pincodes in India!
                              </p>
                            </div>
                          </div>

                          {/* 5. Smart Rate Card */}
                          <div className="w-full mb-6 text-left">

                            {/* Big Active Rate Display */}
                            <div className="mb-4 rounded-2xl relative overflow-hidden border transition-all duration-500 border-cyan-500/30 shadow-[0_0_30px_rgba(6,182,212,0.12),inset_0_1px_0_rgba(6,182,212,0.1)]">
                              {/* Background layers */}
                              <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/80 via-white/90 to-blue-50/80 dark:from-cyan-950/50 dark:via-[#0a0f1e] dark:to-blue-950/30" />
                              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />
                              <div className="absolute -top-10 -right-10 w-40 h-40 bg-cyan-500/[0.08] rounded-full blur-2xl pointer-events-none" />
                              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-blue-500/[0.06] rounded-full blur-xl pointer-events-none" />

                              {/* Top bar: label + badges */}
                              <div className="relative flex items-center justify-between px-4 pt-3.5 pb-3 border-b border-black/[0.06] dark:border-white/[0.06]">
                                <div className="flex items-center gap-2">
                                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 dark:bg-cyan-400 shadow-[0_0_6px_rgba(6,182,212,0.8)]" />
                                  <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">Your Rate Per Page</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-[9px] font-black text-cyan-700 dark:text-cyan-300 bg-cyan-500/10 dark:bg-cyan-950/70 border border-cyan-500/30 dark:border-cyan-400/30 px-2.5 py-1 rounded-full tracking-wide">
                                    ✦ First Order Diamond Deal
                                  </span>
                                </div>
                              </div>

                              {/* Rate panels */}
                              <div className="relative grid grid-cols-2 divide-x divide-black/[0.06] dark:divide-white/[0.06]">
                                {/* B&W Panel */}
                                <div className="px-5 py-4 flex flex-col gap-1 relative group">
                                  <span className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-1">Black &amp; White</span>
                                  <div className="flex items-baseline gap-1">
                                    <span className="text-4xl sm:text-5xl font-black leading-none tracking-tight text-cyan-600 dark:text-cyan-200"
                                      style={{ textShadow: '0 0 20px rgba(6,182,212,0.3)' }}>
                                      {getBwRate('lifetime')}
                                    </span>
                                  </div>
                                  <span className="text-[10px] text-text-muted font-medium mt-0.5">per page · duplex</span>
                                  <div className="absolute bottom-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />
                                </div>

                                {/* Color Panel */}
                                <div className="px-5 py-4 flex flex-col gap-1 relative">
                                  <span className="text-[9px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-400/80 mb-1">Full Color</span>
                                  <div className="flex items-baseline gap-1">
                                    <span className="text-4xl sm:text-5xl font-black leading-none tracking-tight text-emerald-600 dark:text-emerald-400"
                                      style={{ textShadow: '0 0 20px rgba(52,211,153,0.3)' }}>
                                      {getColorRate('lifetime')}
                                    </span>
                                  </div>
                                  <span className="text-[10px] text-text-muted font-medium mt-0.5">per page · duplex</span>
                                </div>
                              </div>
                            </div>

                            {/* 6. Print & Deliver CTA Button */}
                            <div className="flex w-full justify-center lg:justify-start mb-6">
                              {isLoading ? (
                                <div className="w-full h-[58px] bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-2xl animate-pulse" />
                              ) : !isAuthenticated ? (
                                <div className="w-full">
                                  <button
                                    onClick={signIn}
                                    className="group relative inline-flex items-center justify-center px-8 py-4 text-base md:text-lg font-semibold text-white bg-gradient-to-r from-primary to-secondary rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.03] shadow-lg w-full whitespace-nowrap cursor-pointer"
                                  >
                                    <span className="relative whitespace-nowrap">
                                      Sign in with Google
                                    </span>
                                  </button>
                                </div>
                              ) : (
                                <div className="w-full">
                                  <a
                                    href="https://print.notescrafter.com/order/upload"
                                    onClick={() => {
                                      trackEvent('print_redirect_clicked', {
                                        source: 'landing_page_showcase_cta',
                                        device_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
                                      });
                                    }}
                                    className="group relative inline-flex items-center justify-center px-8 py-4 text-base md:text-lg font-bold text-white bg-gradient-to-r from-primary to-secondary rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.03] shadow-lg w-full whitespace-nowrap cursor-pointer uppercase tracking-wider"
                                  >
                                    <span className="relative whitespace-nowrap">
                                      UPLOAD PRINT READY / LECTURE PDF
                                    </span>
                                  </a>
                                </div>
                              )}
                            </div>

                            {/* 5-Column Pass Comparison Grid */}
                            <div className="flex items-center justify-between mb-2 pl-1">
                              <h3 className="text-[10px] font-bold uppercase tracking-wider text-text-muted">
                                Your Printing Rates
                              </h3>
                            </div>
                            <div className="grid grid-cols-5 gap-1.5 md:gap-2 items-stretch">
                              {[
                                { key: 'guest', name: 'Guest', bw: getBwRate('guest'), color: getColorRate('guest'), colorClass: 'border-black/10 dark:border-white/10 from-black/[0.03] to-black/[0.03] dark:from-white/5 dark:to-white/5 text-text-main', activeGlow: 'shadow-[0_0_15px_rgba(0,0,0,0.06)] dark:shadow-[0_0_15px_rgba(255,255,255,0.06)]' },
                                { key: 'monthly', name: 'Silver', bw: getBwRate('monthly'), color: getColorRate('monthly'), colorClass: 'border-slate-300 dark:border-slate-400/20 from-slate-100 dark:from-slate-400/10 via-slate-50 dark:via-slate-500/5 to-slate-100 dark:to-slate-400/10 text-slate-800 dark:text-slate-300', activeGlow: 'shadow-[0_0_15px_rgba(148,163,184,0.2)]' },
                                { key: 'half_yearly', name: 'Gold', bw: getBwRate('half_yearly'), color: getColorRate('half_yearly'), colorClass: 'border-amber-300 dark:border-amber-500/20 from-amber-50 dark:from-amber-500/10 via-amber-100/50 dark:via-amber-600/5 to-amber-50 dark:to-amber-500/10 text-amber-900 dark:text-amber-200', activeGlow: 'shadow-[0_0_15px_rgba(245,158,11,0.2)]' },
                                { key: 'annual', name: 'Platinum', bw: getBwRate('annual'), color: getColorRate('annual'), colorClass: 'border-teal-300 dark:border-teal-500/20 from-teal-50 dark:from-teal-500/10 via-teal-100/50 dark:via-teal-600/5 to-teal-50 dark:to-teal-500/10 text-teal-900 dark:text-teal-200', activeGlow: 'shadow-[0_0_15px_rgba(20,184,166,0.2)]' },
                                { key: 'lifetime', name: 'Diamond', bw: getBwRate('lifetime'), color: getColorRate('lifetime'), colorClass: 'border-cyan-400 dark:border-cyan-500/25 from-cyan-50 dark:from-cyan-600/15 via-blue-50 dark:via-blue-500/10 to-cyan-50 dark:to-cyan-600/15 text-cyan-950 dark:text-cyan-200', activeGlow: 'shadow-[0_0_20px_rgba(6,182,212,0.3)] border-cyan-400', isDiamond: true },
                              ].map((tier) => {
                                const isActive = !!tier.isDiamond;
                                return (
                                  <div
                                    key={tier.name}
                                    className={`flex flex-col rounded-xl border p-2 text-center transition-all duration-300 relative bg-gradient-to-br ${isActive
                                        ? `${tier.colorClass} ${tier.activeGlow} ring-1 ring-emerald-500/20 scale-[1.04]`
                                        : 'border-black/5 dark:border-white/5 bg-black/[0.02] dark:bg-white/5 text-text-muted scale-[0.96]'
                                      }`}
                                  >
                                    {isActive && (
                                      <div className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-emerald-400" style={{ boxShadow: '0 0 6px rgba(52,211,153,0.9)' }} />
                                    )}

                                    {/* Row 1: Pass Name */}
                                    <div className="pb-1.5 border-b border-black/5 dark:border-white/10 flex flex-col justify-between min-h-[44px]">
                                      <span className="text-[7px] text-text-muted/60 font-bold uppercase tracking-wider block">Pass</span>
                                      <span className={`text-[9px] font-black tracking-tight ${isActive ? 'text-text-main' : 'text-text-muted'}`}>
                                        {tier.name}
                                      </span>
                                    </div>

                                    {/* Row 2: Black & White Rate */}
                                    <div className="py-1.5 border-b border-black/5 dark:border-white/5 flex flex-col justify-center">
                                      <span className="text-[7px] text-text-muted/60 font-bold uppercase tracking-wider block">B&W</span>
                                      <span className={`text-[11px] font-black ${isActive ? 'text-text-main' : 'text-text-main/80'}`}>{tier.bw}</span>
                                    </div>

                                    {/* Row 3: Color Rate */}
                                    <div className="pt-1.5 flex flex-col justify-center">
                                      <span className="text-[7px] text-text-muted/60 font-bold uppercase tracking-wider block">Color</span>
                                      <span className={`text-[11px] font-black ${isActive ? 'text-emerald-600 dark:text-emerald-400 font-extrabold' : 'text-emerald-600/80 dark:text-emerald-400/80'}`}>{tier.color}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 7. Expanding Steps Accordion — 100% Full Width just below Left & Right columns */}
                      <div className="w-full mt-6 sm:mt-8 border border-black/10 dark:border-white/10 rounded-2xl bg-white/70 dark:bg-[#0d0d16] overflow-hidden text-left divide-y divide-black/10 dark:divide-white/10 shadow-lg">
                        {PRINT_PROCESS_STEPS.map((item, idx) => {
                          const Icon = item.icon;
                          const isOpen = expandedAccordion === idx;
                          return (
                            <div
                              key={item.id}
                              className={`transition-all duration-300 ${isOpen ? 'bg-primary/5 dark:bg-white/5 border-l-2 border-primary' : 'bg-transparent border-l-2 border-transparent'}`}
                            >
                              <button
                                onClick={() => {
                                  setUserInteracted(true);
                                  setCtaPaused(true);
                                  setExpandedAccordion(isOpen ? null : idx);
                                }}
                                className="w-full px-5 sm:px-6 py-4 flex items-center justify-between text-text-main hover:bg-black/[0.03] dark:hover:bg-white/5 transition-colors focus:outline-none cursor-pointer"
                              >
                                <div className="flex items-center gap-3">
                                  <Icon className={`w-5 h-5 ${isOpen ? 'text-primary' : 'text-text-muted'}`} />
                                  <span className="text-sm sm:text-base font-semibold tracking-wide">{item.title}</span>
                                </div>
                                <ChevronDown className={`w-4 h-4 text-text-muted transition-transform duration-300 ${isOpen ? 'rotate-180 text-primary' : ''}`} />
                              </button>
                              <AnimatePresence initial={false}>
                                {isOpen && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                                    className="overflow-hidden"
                                  >
                                    <ul className="px-5 sm:px-6 pb-5 pt-3 border-t border-black/5 dark:border-white/5 flex flex-col gap-2.5">
                                      {item.points.map((point, pi) => (
                                        <li key={pi} className="flex items-start gap-2.5 text-xs sm:text-sm text-text-muted leading-relaxed">
                                          <span className="mt-0.5 text-emerald-600 dark:text-emerald-400 font-bold shrink-0 text-xs">✓</span>
                                          <span>{point}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}
                      </div>
                    </GlowCard>
                </motion.div>
              )}

              {/* 2. FLOW CTA Container (3:7 Layout — 3 for text/CTA, 7 for video) */}
              {activeCtaTab === 'flow' && (
                <motion.div
                  key="flow"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="w-full"
                >
                  <GlowCard className="w-full rounded-2xl" contentClassName="p-4 sm:p-6 lg:p-10 shadow-2xl rounded-[11px]">
                    <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12 py-2">
                      {/* Left: Text & CTA (30-35% width) */}
                      <div className="w-full lg:w-[32%] flex flex-col items-center lg:items-start text-center lg:text-left justify-center space-y-4">
                        <div className="mb-1">
                          <AIBadge />
                        </div>
                        
                        <h3 className="hero-heading text-lg sm:text-xl md:text-2xl lg:text-3xl font-extrabold tracking-tight leading-tight">
                          Make Your Notes{' '}
                          <span className="printable animate-gradient">Printable</span>
                        </h3>

                        <p className="text-text-muted text-xs sm:text-sm lg:text-base leading-relaxed font-light">
                          An intelligent multi-step workflow designed to enhance dark lecture slides into perfect printable notes, create study-ready notes in seconds.
                        </p>

                        {/* Start Processing FLOW CTA */}
                        <div className="w-full pt-2">
                          <a
                            href="https://flow.notescrafter.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              trackEvent('start_processing_clicked', {
                                source: 'landing_page_showcase_flow',
                                device_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
                              });
                            }}
                            className="group relative w-full h-[60px] overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold px-4 rounded-xl shadow-lg shadow-purple-900/40 hover:shadow-xl transition-all duration-300 transform hover:scale-[1.01] flex items-center justify-start gap-4"
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                            
                            <div className="flex flex-col items-start text-left leading-tight pr-20">
                              <span className="text-sm font-extrabold tracking-wide flex items-center gap-1.5 justify-start">
                                Start Processing
                                <svg className="w-3.5 h-3.5 text-purple-200/90 shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                </svg>
                              </span>
                              <span className="text-[10px] text-purple-200/90 font-medium mt-0.5">Upload Dark slides and download printable notes</span>
                            </div>

                            <span className="absolute right-4 top-1/2 -translate-y-1/2 shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-[9px] font-extrabold bg-green-600 text-white shadow-[0_2px_8px_rgba(22,163,74,0.35)] select-none border border-green-500">
                              Free Access
                            </span>
                          </a>
                        </div>
                      </div>

                      {/* Right: Video Showcase (68-70% width) */}
                      <div className="w-full lg:w-[68%] flex items-center justify-center">
                        <div className="w-full rounded-2xl overflow-hidden border border-black/10 dark:border-white/10 shadow-2xl bg-black/5 dark:bg-black/40 p-1 sm:p-2 backdrop-blur-md relative group/video">
                          <div className="relative rounded-xl overflow-hidden">
                            <ProductShowcaseVideo
                              videoSrc="/ProductShowcaseVideo.webm"
                              posterSrc="/Showcase.webp"
                              alt="NotesCrafter Showcase - PDF Processing Demo"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </GlowCard>
                </motion.div>
              )}

              {/* 3. TOOLS CTA Container (L-shape merged on Right) */}
              {activeCtaTab === 'tools' && (
                <motion.div
                  key="tools"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="w-full"
                >
                  <GlowCard className="w-full rounded-tr-none rounded-tl-2xl rounded-b-2xl" contentClassName="p-4 sm:p-6 lg:p-10 shadow-2xl flex flex-col justify-between rounded-tr-none rounded-tl-[11px] rounded-b-[11px]">
                    <div className="w-full">
                      {/* Top Header inside TOOLS */}
                      <div className="flex items-center justify-between w-full mb-6">
                        <div className="flex items-center gap-2">
                          <span className="text-base sm:text-lg font-extrabold text-text-main tracking-tight">
                            Quick PDF &amp; Study Tools
                          </span>
                          <span className="text-[10px] text-text-muted font-medium hidden sm:inline-block">
                            · Click any tool to launch directly
                          </span>
                        </div>
                        <span className="text-[10px] uppercase tracking-widest text-primary font-extrabold bg-primary/10 dark:bg-purple-950/60 px-3 py-1 rounded-full border border-primary/20 dark:border-purple-500/30 select-none">
                          10 Free Tools
                        </span>
                      </div>

                      {/* Tools Grid: 10 Tools with Premium Minimal Lightweight Icons and Direct Clickable Links */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 lg:gap-4 my-2">
                        {PDF_TOOLS_LIST.map((tool) => {
                          const Icon = tool.icon;
                          return (
                            <a
                              key={tool.id}
                              href={tool.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => {
                                trackEvent('tools_item_clicked', {
                                  tool_id: tool.id,
                                  tool_name: tool.title,
                                  source: 'landing_page_tools_grid'
                                });
                              }}
                              className="group relative flex flex-col items-center justify-between p-4 sm:p-5 rounded-2xl bg-black/[0.02] dark:bg-white/[0.03] border border-black/10 dark:border-white/10 hover:border-primary/40 hover:bg-black/[0.05] dark:hover:bg-white/[0.06] transition-all duration-300 text-center hover:scale-[1.03] hover:shadow-xl hover:shadow-primary/10 cursor-pointer select-none"
                            >
                              {/* Open in new tab arrow badge on hover */}
                              <div className="absolute top-2.5 right-2.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                <ArrowUpRight className="w-3.5 h-3.5 text-primary" />
                              </div>

                              {tool.badge && (
                                <div className="absolute top-2.5 left-2.5">
                                  <span className="text-[8px] font-extrabold uppercase px-1.5 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/25">
                                    {tool.badge}
                                  </span>
                                </div>
                              )}

                              {/* Minimal Lightweight Icon Container */}
                              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tool.color} bg-black/[0.02] dark:bg-white/[0.03] border border-black/10 dark:border-white/10 group-hover:border-primary/40 flex items-center justify-center mb-3 mt-1 group-hover:scale-110 transition-all duration-300 shadow-sm group-hover:shadow-[0_0_15px_rgba(168,85,247,0.25)]`}>
                                <Icon className="w-5 h-5 stroke-[1.75]" />
                              </div>

                              {/* Title & Description */}
                              <div className="flex flex-col items-center gap-1 w-full">
                                <div className="text-sm font-bold text-text-main group-hover:text-primary transition-colors">
                                  {tool.title}
                                </div>
                                <div className="text-[11px] text-text-muted group-hover:text-text-main font-medium leading-tight line-clamp-2">
                                  {tool.desc}
                                </div>
                              </div>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  </GlowCard>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

          {/* Video Demonstration Section - Below the 3 CTA Slideshow */}
          <div className="w-full px-3 sm:px-6 lg:px-10 my-10 md:my-14 relative z-10">
            <div className="relative rounded-[2rem] overflow-hidden border border-black/10 dark:border-white/10 bg-white/60 dark:bg-black/40 backdrop-blur-xl p-1 shadow-2xl shadow-purple-950/10 group hover:border-primary/30 transition-all duration-500">
              {/* Background gradient glow on hover */}
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-transparent to-indigo-500/10 opacity-50 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
              
              <div className="relative w-full aspect-video rounded-[1.8rem] overflow-hidden bg-black/50">
                <iframe
                  src="https://www.youtube.com/embed/pS3wCZcXGq4"
                  title="NotesCrafter Print Video Showcase"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>
          </div>

          {/* How it works section on the landing page */}
          <div className="w-full px-3 sm:px-6 lg:px-10 mt-10">
            <HowItWorks />
          </div>
        </div>
      </main>

      {/* Footer - render only when used as a standalone page (avoid duplicates in App shell) */}
      {isStandalone && (
        <div className="mt-auto">
          <Footer onNavigate={onNavigate} />
        </div>
      )}
      {/* Zoom Modal Overlay matching notescrafter-print */}
      <AnimatePresence>
        {isZoomed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center select-none"
            onClick={() => setIsZoomed(false)}
          >
            {/* Close Cross Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsZoomed(false);
              }}
              className="absolute top-6 right-6 z-50 flex items-center justify-center w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white backdrop-blur-md transition-all hover:scale-105 active:scale-95 shadow-lg cursor-pointer"
              aria-label="Close zoom"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Left Navigation Arrow */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPrintsInteracted(true);
                setPrintsSlideIndex((prev) => (prev - 1 + PRINTS_SLIDES.length) % PRINTS_SLIDES.length);
              }}
              className="absolute left-6 top-1/2 -translate-y-1/2 z-50 flex items-center justify-center w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white backdrop-blur-md transition-all hover:scale-105 active:scale-95 shadow-lg cursor-pointer"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Main Zoomed Image Container */}
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="relative max-w-[90vw] max-h-[80vh] flex items-center justify-center rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-black/40"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={PRINTS_SLIDES[printsSlideIndex].src}
                alt={PRINTS_SLIDES[printsSlideIndex].label || 'Notebook Preview'}
                className="max-w-full max-h-[80vh] object-contain rounded-2xl"
              />

              {/* Floating Slide Counter Badge */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-xl bg-black/60 border border-white/10 text-white text-xs sm:text-sm font-semibold tracking-wide backdrop-blur-md shadow-lg flex items-center gap-2">
                <span>{printsSlideIndex + 1} / {PRINTS_SLIDES.length}</span>
              </div>
            </motion.div>

            {/* Right Navigation Arrow */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPrintsInteracted(true);
                setPrintsSlideIndex((prev) => (prev + 1) % PRINTS_SLIDES.length);
              }}
              className="absolute right-6 top-1/2 -translate-y-1/2 z-50 flex items-center justify-center w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white backdrop-blur-md transition-all hover:scale-105 active:scale-95 shadow-lg cursor-pointer"
              aria-label="Next image"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ModernLandingPage;